/**
 * Custom CKEditor Plugin for Font Background Color UI
 * 
 * This plugin provides a custom dropdown UI for selecting background colors for text. 
 * It includes a grid of color buttons, a remove color button, and integration with the 
 * editor's command to apply the selected background color.
 * 
 * ## Key Features
 * - **Dropdown UI**: A custom dropdown with a grid of color options.
 * - **Remove Color Button**: Clears the background color selection.
 * - **Arrow Icon**: Indicates the dropdown's toggle state.
 * - **Dynamic Colors**: Reads the color configuration from the editor's settings.
 * 
 * ### Core Components
 * - **Color Button List**: Dynamically generates a grid of buttons based on the editor's configuration.
 * - **Remove Button**: Allows users to remove the applied background color.
 * - **Dropdown Interaction**: Handles opening and closing of the dropdown.
 * - **Command Integration**: Executes the associated command with the selected color value.
 * 
 * ### Events
 * - **Focus Tracking**: Ensures dropdown closes when the editor or dropdown loses focus.
 * - **State Management**: Updates the UI state when a color is selected or removed.
 * 
 * ### References
 * - CKEditor Plugin API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/framework/guides/creating-simple-plugin.html
 * - CKEditor UI Component API:
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_componentfactory-ComponentFactory.html
 * 
 * @module CustomBackgroundFontColorUI
 * @exports CustomBackgroundFontColorUI
 */

import { CKPlugins } from '../../constants/constants';

const {
    DropdownView,
    DropdownPanelView,
    ButtonView,
    Plugin,
    IconView,
    View
} = window.ClassicEditor;

const fontColorIcon = '<svg class="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color ck-button__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M4 2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm8.38 9.262H7.62L10 5.506l2.38 5.756zm.532 1.285L14.34 16h1.426L10.804 4H9.196L4.234 16H5.66l1.428-3.453h5.824z"></path></svg>';
const arrowDownIcon = '<svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M.941 4.523a.75.75 0 1 1 1.06-1.06l3.006 3.005 3.005-3.005a.75.75 0 1 1 1.06 1.06l-3.549 3.55a.75.75 0 0 1-1.168-.136L.941 4.523z"></path></svg>';
const removeIcon = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M8.636 9.531l-2.758 3.94a.5.5 0 0 0 .122.696l3.224 2.284h1.314l2.636-3.736L8.636 9.53zm.288 8.451L5.14 15.396a2 2 0 0 1-.491-2.786l6.673-9.53a2 2 0 0 1 2.785-.49l3.742 2.62a2 2 0 0 1 .491 2.785l-7.269 10.053-2.147-.066z"></path><path d="M4 18h5.523v-1H4zm-2 0h1v-1H2z"></path></svg>';
const buttonIcon = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M16.935 5.328a2 2 0 0 1 0 2.829l-7.778 7.778a2 2 0 0 1-2.829 0L3.5 13.107a1.999 1.999 0 1 1 2.828-2.829l.707.707a1 1 0 0 0 1.414 0l5.658-5.657a2 2 0 0 1 2.828 0z" class="ck-icon__fill" style="fill: rgb(255, 255, 255);"></path><path d="M14.814 6.035L8.448 12.4a1 1 0 0 1-1.414 0l-1.413-1.415A1 1 0 1 0 4.207 12.4l2.829 2.829a1 1 0 0 0 1.414 0l7.778-7.778a1 1 0 1 0-1.414-1.415z"></path></svg>';

export class CustomBackgroundFontColorUI extends Plugin {
    icon: any;
    commandName: any;
    componentName: any;
    dropdownLabel: any;
    removeButtonLabel: any;
    editor: any;
    dropdownView: any;
    colorButtonList: any[] = [];
    arrowView: any;
    removeColorButton: any;

    constructor(editor) {
        super(editor);

        const t = editor.locale.t;
        this.icon = fontColorIcon;
        this.commandName = CKPlugins.CustomFontBackgroundColor;
        this.componentName = CKPlugins.CustomFontBackgroundColor;
        this.dropdownLabel = t('Font Background Color');
        this.removeButtonLabel = t('Remove Color');
    }

    static get pluginName() {
        return 'CustomBackgroundFontColorUI';
    }

    init() {
        const editor = this.editor;
        const colors = editor.config.get(this.componentName).colors;
        const locale = editor.locale;

        this.arrowView = this.createArrowView();
        this.removeColorButton = this.createRemoveColorButton();
        this.dropdownView = new DropdownView(locale, new ButtonView(), new DropdownPanelView());
        this.dropdownView.buttonView.set({
            icon: fontColorIcon,
            label: this.dropdownLabel,
            tooltip: true,
        });

        this.dropdownView.buttonView.on('execute', () => this.toggleDrodown());

        this.dropdownView.class = 'ck-dropdown_bg_color';

        this.dropdownView.panelView.children.add(this.removeColorButton);
        if (this.dropdownView.buttonView.isRendered) {
            this.dropdownView.buttonView.children.add(this.arrowView);
        } else {
            this.dropdownView.buttonView.once('render', () => {
                this.dropdownView.buttonView.children.add(this.arrowView);
            });
        }

        const viewStyleAttribute = {
            gridTemplateColumns: `repeat( ${ 2 }, 1fr)`
        };

        this.colorButtonList = this.createColorButtonList(editor, locale, colors);

        const view = new View();
        view.setTemplate({
            tag: 'div',
            children: this.colorButtonList,
            attributes: {
                class: [
                    'ck',
                    'ck-color-grid'
                ],
                style: viewStyleAttribute
            }
        });

        this.dropdownView.panelView.children.add(view);
        editor.ui.componentFactory.add(CKPlugins.CustomFontBackgroundColor, () => this.dropdownView);

        editor.ui.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
            if (!isFocused) {
                this.dropdownView.isOpen = false;
            }
        });

        this.dropdownView.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
            if (!isFocused) {
                this.dropdownView.isOpen = false;
            }
        });

        this.dropdownView.on('change:isOpen', (evt, name, isOpen) => {
            const element = this.dropdownView.buttonView.element;
            isOpen ? this.selectColorButton(element) : this.unselectColorButton(element);
        });
    }

    toggleDrodown() {
        this.dropdownView.isOpen = !this.dropdownView.isOpen;
    }

    private createRemoveColorButton() {
        const buttonView = new ButtonView();

        buttonView.set( {
            withText: true,
            icon: removeIcon,
            label: this.removeButtonLabel
        } );

        buttonView.class = 'ck-color-selector__remove-color';
        buttonView.on('execute', () => {
            this.unselectAllColorButtons();
            this.toggleDrodown();
            this.editor.execute(this.commandName, { value: null });
        } );

        buttonView.render();

        return buttonView;
    }

    private createColorButtonList(editor: any, locale: any, colors: any[]) {
        return colors.map(color => {
            const buttonView = new ButtonView(locale);
            buttonView.set({
                label: color.name,
                withText: true,
                icon: buttonIcon,
                class: color.class + ' ck-color-grid__tile ck-color-table__color-tile_bordered'
            });

            buttonView.on('execute', () => {
                this.unselectAllColorButtons();
                this.selectColorButton(buttonView.element);
                this.toggleDrodown();
                editor.execute(this.commandName, { value: color.class });
            });

            return buttonView;
        });
    }

    private createArrowView() {
        const arrowView = new IconView();

        arrowView.content = arrowDownIcon;

        arrowView.extendTemplate( {
            attributes: {
                class: 'ck-dropdown__arrow'
            }
        } );

        return arrowView;
    };

    private unselectAllColorButtons = () => {
        this.colorButtonList.map(item => {
            this.unselectColorButton(item.element);
        });
    };

    private selectColorButton = (element: Element) => {
        element.classList.add('ck-on');
        element.classList.remove('ck-off');
    };

    private unselectColorButton = (element: Element) => {
        element.classList.add('ck-off');
        element.classList.remove('ck-on');
    };
}
